//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import {
  BodyBoldText,
  BodySecondaryText,
  BodyText,
} from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { getIsUiaVideoMuteEnabled } from '@legacy-components/common/universal-image-actions/selectors/getIsUiaVideMuteEnabled';
import {
  MarketoIntegration,
  MarketoIntegrationModalStyle,
} from '@marketo-integration/MarketoIntegration';
import { MarketoIntegrationFeature } from '@marketo-integration/MarketoIntegrationFeature';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';

import { muteTooltipMessage } from 'app/constants/mute-tooltip-content';

export interface UniversalImageActionsAudioButtonProps {
  isDisabled: boolean;
  onClickMuteUnmuteItems: () => void;
  muteStatus: boolean;
}

const UniversalImageActionsAudioButton = ({
  isDisabled,
  onClickMuteUnmuteItems,
  muteStatus,
}: UniversalImageActionsAudioButtonProps) => {
  const isUiaVideoMuteEnabled = useSelector(getIsUiaVideoMuteEnabled);

  return (
    <>
      <Popover
        target={
          <StyledActionButton
            iconName={muteStatus ? 'volume-off' : 'volume-on'}
            onClick={onClickMuteUnmuteItems}
            data-status={muteStatus}
            isDisabled={isDisabled || !isUiaVideoMuteEnabled}
          />
        }
        content={
          <PopoverContentContainer>
            {isUiaVideoMuteEnabled && (
              <BodyBoldText>{muteStatus ? 'Unmute' : 'Mute'}</BodyBoldText>
            )}
            {!muteStatus &&
              (isUiaVideoMuteEnabled ? (
                <BodySecondaryText>{muteTooltipMessage}</BodySecondaryText>
              ) : (
                <BodyText>
                  Upgrade to unlock video sound control and the ability to mute
                  videos.&nbsp;
                  <MarketoIntegration
                    feature={MarketoIntegrationFeature.UIAVideoMute}
                  />
                </BodyText>
              ))}
          </PopoverContentContainer>
        }
        placement='bottom'
      />
      <MarketoIntegrationModalStyle />
    </>
  );
};

angular.module('curalate').component('universalImageActionsAudioButton', {
  bindings: {
    isDisabled: '<',
    onClickMuteUnmuteItems: '&',
    muteStatus: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: UniversalImageActionsAudioButtonProps } & IScope) => {
      return (
        <UniversalImageActionsAudioButton
          isDisabled={scope.$ctrl.isDisabled}
          onClickMuteUnmuteItems={applyScope(
            scope,
            scope.$ctrl.onClickMuteUnmuteItems,
          )}
          muteStatus={scope.$ctrl.muteStatus}
        />
      );
    },
  ),
});

const StyledActionButton = styled(ActionButton)(
  ({ theme }) => `
    :hover {
      
      button[disabled] {
        background-color: ${theme.colors.white};
        
        svg {
          color: ${theme.colors.gray7};
        }
      }
    }
  `,
);

const PopoverContentContainer = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[2]};
  max-width: ${theme.smallMeasure};
`,
);
